import { useEffect, useCallback } from 'react';
import { Database, Layers, Network } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { StatusItem } from '@/components/ui/status-item';
import { cn } from '@/lib/utils';
import { useApiToken } from '@/auth/hooks';
import {
  ConnectionType,
  useConnectionStatusStore,
} from '@/stores/useConnectionStatusStore';
import { useConnectorStore } from '@/stores/useConnectorStore';
import { ConnectionDetailPopover } from './ConnectionDetailPopover';
import { ConnectorStatusChips } from './ConnectorStatusChips';

/* ------------------------------------------------------------------ */
/*  Icon & label map                                                   */
/* ------------------------------------------------------------------ */

const DB_ICON: Record<ConnectionType, typeof Database> = {
  [ConnectionType.PostgreSQL]: Database,
  [ConnectionType.Neo4j]:      Network,
  [ConnectionType.Qdrant]:     Layers,
};

const DB_LABEL: Record<ConnectionType, string> = {
  [ConnectionType.PostgreSQL]: 'PostgreSQL',
  [ConnectionType.Neo4j]:      'Neo4j',
  [ConnectionType.Qdrant]:     'Qdrant',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * Renders compact connection-status indicators for Neo4j, Qdrant and
 * PostgreSQL inside the application footer. Fetches status from the
 * backend every 30 s and shows a detail popover on click.
 */
export function ConnectionStatusIndicator() {
  const { getAccessToken } = useApiToken();
  const {
    connections,
    checkedAt,
    isLoading,
    error,
    fetchStatus,
    startPolling,
    stopPolling,
  } = useConnectionStatusStore();
  const connectors = useConnectorStore((s) => s.connectors);

  // Start polling on mount, stop on unmount
  useEffect(() => {
    startPolling(getAccessToken, 30_000);
    return () => stopPolling();
  }, [getAccessToken, startPolling, stopPolling]);

  const handleRefresh = useCallback(() => {
    void getAccessToken().then((token) => {
      if (token) fetchStatus(token);
    });
  }, [getAccessToken, fetchStatus]);

  const hasError = Boolean(error) && !isLoading;
  const noneLoaded = connections.length === 0 && !isLoading && !hasError;

  return (
    <ConnectionDetailPopover
      connections={connections}
      checkedAt={checkedAt}
      isLoading={isLoading}
      error={error}
      onRefresh={handleRefresh}
    >
      <div className="flex items-center">
        {connections.length === 0 ? (
          <StatusItem
            icon={Database}
            tooltip="Database connections"
            className={hasError ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}
          >
            {isLoading
              ? 'Checking…'
              : hasError
                ? 'DB status error'
                : noneLoaded
                  ? 'No status'
                  : 'Loading…'}
          </StatusItem>
        ) : (
          connections
            .slice()
            .sort((a, b) => a.type - b.type)
            .map((conn, idx) => {
              const Icon = DB_ICON[conn.type] ?? Database;
              const label = DB_LABEL[conn.type] ?? conn.name;
              return (
                <div key={`${conn.type}-${conn.name}`} className="flex items-center">
                  {idx > 0 && (
                    <Separator
                      orientation="vertical"
                      className="h-4 mx-0.5 bg-border/50"
                    />
                  )}
                  <StatusItem
                    icon={Icon}
                    tooltip={`${label}: ${conn.isConnected ? 'Connected' : 'Disconnected'}${conn.error ? ` – ${conn.error}` : ''}`}
                    highlight={conn.isConnected}
                    className={cn(
                      'font-medium',
                      conn.isConnected
                        ? 'text-emerald-600 dark:text-sopho-process'
                        : 'text-red-600 dark:text-red-400',
                    )}
                  >
                    {label}: {conn.isConnected ? 'Connected' : 'Disconnected'}
                  </StatusItem>
                </div>
              );
            })
        )}
        {connectors.length > 0 && (
          <>
            <Separator orientation="vertical" className="h-4 mx-0.5 bg-border/50" />
            <ConnectorStatusChips />
          </>
        )}
      </div>
    </ConnectionDetailPopover>
  );
}
